import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import { cn } from '../utils/cn';

export interface SearchableDropdownItem {
  id: string | number;
  label: string;
  subtitle?: string;
}

/** Imperative handle to open/close the dropdown from a parent (e.g. from an alert). */
export interface SearchableDropdownHandle {
  open: () => void;
  close: () => void;
}

interface SearchableDropdownProps {
  /** The button/pill that triggers the dropdown */
  trigger: React.ReactNode;
  /** All items (showAll mode) or initial items (waitForInput mode) */
  items?: SearchableDropdownItem[];
  /**
   * showAll: renders full list alphabetically on open, input filters it
   * waitForInput: shows empty input on open, only searches after typing
   */
  mode: 'showAll' | 'waitForInput';
  /** Async search fn — used in waitForInput mode */
  onSearch?: (query: string) => Promise<SearchableDropdownItem[]>;
  searchPlaceholder?: string;
  /** Called when an item is selected */
  onSelect: (item: SearchableDropdownItem) => void;
  /** ID of the currently selected item (hides it from the standard list) */
  selectedId?: string | number | null;
  /** Custom row renderer — replaces the default button per item */
  renderItem?: (item: SearchableDropdownItem, onSelect: () => void, isActive?: boolean) => React.ReactNode;
  /** Optional content rendered above the list */
  header?: React.ReactNode;
  /** Optional content rendered below the list */
  footer?: React.ReactNode;
  disabled?: boolean;
  className?: string;
  /** Min width of the dropdown panel */
  minWidth?: number;
  isLoading?: boolean;
  /** Called when the dropdown opens */
  onOpen?: () => void;
  /** Called when the dropdown closes */
  onClose?: () => void;
  /** Whether to autofocus the search input when the dropdown opens (default true) */
  autoFocusSearch?: boolean;
}

export const SearchableDropdown = forwardRef<SearchableDropdownHandle, SearchableDropdownProps>(({
  trigger,
  items = [],
  mode,
  onSearch,
  searchPlaceholder = 'Buscar...',
  onSelect,
  selectedId,
  renderItem,
  footer,
  header,
  disabled = false,
  className,
  minWidth = 280,
  isLoading = false,
  onOpen,
  onClose,
  autoFocusSearch = true,
}, ref) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [asyncResults, setAsyncResults] = useState<SearchableDropdownItem[]>([]);
  const [searching, setSearching] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const sortedItems = [...items].sort((a, b) => {
    if (a.label === 'Sin lista') return -1;
    if (b.label === 'Sin lista') return 1;
    return String(a.label ?? '').localeCompare(String(b.label ?? ''), 'es');
  });

  const displayItems = (() => {
    const source = mode === 'waitForInput' ? asyncResults : sortedItems;
    const filtered = query.trim()
      ? source.filter(
          (item) =>
            item.label.toLowerCase().includes(query.toLowerCase()) ||
            item.subtitle?.toLowerCase().includes(query.toLowerCase())
        )
      : source;
    return filtered.filter((item) => item.id !== selectedId);
  })();

  const openDropdown = () => {
    if (disabled || open) return;
    setOpen(true);
    setQuery('');
    setActiveIndex(-1);
    if (mode === 'waitForInput') setAsyncResults([]);
    onOpen?.();
  };

  const closeDropdown = useCallback(() => {
    setOpen(false);
    setQuery('');
    setActiveIndex(-1);
    setAsyncResults([]);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    onClose?.();
  }, [onClose]);

  // Expose imperative open/close so a parent can open the dropdown (e.g. when the
  // "missing delivery data" alert is clicked) without the user clicking the trigger.
  useImperativeHandle(ref, () => ({ open: openDropdown, close: closeDropdown }));

  useEffect(() => {
    if (open && autoFocusSearch) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open, autoFocusSearch]);

  // Reset highlight when the visible list changes (typing refilters)
  useEffect(() => {
    setActiveIndex(-1);
  }, [query, asyncResults]);

  useEffect(() => {
    if (mode !== 'waitForInput' || !onSearch) return;
    if (!query.trim()) { setAsyncResults([]); return; }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const results = await onSearch(query.trim());
        setAsyncResults(results);
      } catch {
        setAsyncResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, mode, onSearch]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        closeDropdown();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, closeDropdown]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeDropdown();
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex(i => Math.min(i + 1, displayItems.length - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex(i => Math.max(i - 1, 0));
        return;
      }
      if (e.key === 'Enter') {
        setActiveIndex(i => {
          if (i >= 0 && i < displayItems.length) {
            e.preventDefault();
            handleSelect(displayItems[i]);
          }
          return i;
        });
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  // displayItems and handleSelect are stable enough within an open session;
  // including them would cause the effect to re-register on every keystroke.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, closeDropdown, displayItems]);

  // Scroll active item into view inside the list panel
  useEffect(() => {
    if (activeIndex < 0 || !listRef.current) return;
    const el = listRef.current.querySelectorAll<HTMLElement>('[data-kbd-item]')[activeIndex];
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const handleSelect = (item: SearchableDropdownItem) => {
    onSelect(item);
    closeDropdown();
  };

  const showEmptyState =
    !searching &&
    !isLoading &&
    displayItems.length === 0 &&
    (mode === 'showAll' || query.trim().length > 0);

  return (
    <div ref={containerRef} className={cn('relative inline-block', className)}>
      <div onClick={openDropdown}>{trigger}</div>

      {open && (
        <div
          className="absolute z-50 top-full mt-1 right-0 bg-background border border-border rounded-lg shadow-lg overflow-hidden flex flex-col"
          style={{ minWidth }}
        >
          {header && <div className="border-b border-border">{header}</div>}

          <div className="px-3 py-2 border-b border-border">
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={searchPlaceholder}
              className="w-full bg-muted rounded-md px-2 py-1.5 text-xs font-sans text-text placeholder:text-text-subtle outline-none border border-border focus:border-primary transition-colors"
            />
          </div>

          <div ref={listRef} className="max-h-52 overflow-y-auto">
            {(searching || isLoading) ? (
              <div className="flex items-center justify-center py-4">
                <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : showEmptyState ? (
              <p className="text-xs text-text-subtle text-center py-4 px-3">
                {mode === 'waitForInput' && !query.trim() ? 'Escribe para buscar...' : 'Sin resultados'}
              </p>
            ) : mode === 'waitForInput' && !query.trim() ? (
              <p className="text-xs text-text-subtle text-center py-4 px-3">Escribe para buscar...</p>
            ) : (
              displayItems.map((item, idx) =>
                renderItem ? (
                  <div key={item.id} data-kbd-item className={cn(activeIndex === idx && 'bg-muted')}>
                    {renderItem(item, () => handleSelect(item), activeIndex === idx)}
                  </div>
                ) : (
                  <button
                    key={item.id}
                    data-kbd-item
                    type="button"
                    onClick={() => handleSelect(item)}
                    className={cn(
                      'w-full text-left px-3 py-2 transition-colors border-b border-border last:border-b-0 flex flex-col gap-0.5',
                      activeIndex === idx ? 'bg-muted' : 'hover:bg-muted'
                    )}
                  >
                    <span className="font-sans font-medium text-xs text-text truncate">{item.label}</span>
                    {item.subtitle && (
                      <span className="font-sans text-xs text-text-subtle truncate">{item.subtitle}</span>
                    )}
                  </button>
                )
              )
            )}
          </div>

          {footer && <div className="border-t border-border">{footer}</div>}
        </div>
      )}
    </div>
  );
});

SearchableDropdown.displayName = 'SearchableDropdown';
